import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ImageCaptcha = ({ token, onVerified }) => {
  const [challenge, setChallenge] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setSelected([]);
    onVerified(null);
    try {
      const res = await axios.get(`${API}/captcha/new`);
      setChallenge(res.data);
    } catch (e) {
      setError("Could not load CAPTCHA. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [onVerified]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    if (token) return;
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const verify = async () => {
    if (selected.length === 0) return;
    setVerifying(true);
    setError("");
    try {
      const res = await axios.post(`${API}/captcha/verify`, {
        challenge_id: challenge.challenge_id,
        selected,
      });
      if (res.data.success) {
        onVerified(res.data.token);
      } else {
        toast.error("Incorrect selection — here's a new challenge.");
        await load();
      }
    } catch (e) {
      toast.error("Verification failed. Please try again.");
      await load();
    } finally {
      setVerifying(false);
    }
  };

  if (token) {
    return (
      <div
        data-testid="captcha-verified"
        className="flex items-center gap-3 rounded-xl border border-lime-bright/60 bg-forest-50 px-4 py-3.5"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-lime-bright">
          <ShieldCheck className="text-forest" size={18} strokeWidth={2.5} />
        </div>
        <span className="font-body text-sm font-semibold text-forest">
          Verification complete — you're human.
        </span>
      </div>
    );
  }

  return (
    <div data-testid="image-captcha" className="rounded-xl border border-border bg-secondary/50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-body text-xs font-semibold uppercase tracking-wider text-forest-600">
            Security Check
          </div>
          <div className="font-heading text-sm font-bold text-forest" data-testid="captcha-prompt">
            {challenge?.prompt || "Loading challenge…"}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="captcha-refresh-btn"
          aria-label="New challenge"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-forest transition-colors hover:bg-forest-50"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="animate-spin text-forest-600" size={22} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {challenge?.tiles.map((tile, i) => {
            const active = selected.includes(tile.id);
            return (
              <button
                type="button"
                key={tile.id}
                onClick={() => toggle(tile.id)}
                data-testid={`captcha-tile-${i}`}
                className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                  active ? "border-forest ring-2 ring-lime-bright" : "border-transparent hover:border-forest/30"
                }`}
              >
                <img src={tile.url} alt="captcha option" className="h-full w-full object-cover" />
                {active && (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-forest">
                    <Check className="text-lime-bright" size={12} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <p className="mt-3 font-body text-sm text-red-600" data-testid="captcha-error">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={verify}
        disabled={verifying || loading || selected.length === 0}
        data-testid="captcha-verify-btn"
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-forest px-5 py-2.5 font-body text-sm font-semibold text-white transition-colors hover:bg-forest-600 disabled:opacity-50"
      >
        {verifying ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
        Verify
      </button>
    </div>
  );
};
